import {
  compile
} from 'handlebars';
import moment from 'moment';
import {
  ENGAGEMENT__STATE_CLOSED,
  ENGAGEMENT__STATE_OPEN,
  REJECTION_REASON__10X10__NO_MATCH,
  STAGE_CONFIRMATION,
  STATUS_E_10X10,
  STATUS_W_10X10,
  STATUS_W_10X10_ASK_PERMISSION,
  STATUS_W_10X10_CALIBRATION,
  STATUS_W_10X10_NOTIFY_REJECTION,
  STATUS_W_10X10_REVIEW
} from '../../../dictionaries/Engagement.dic';
import {
  sanitizeArr
} from '../../../lib/Array.lib';
import Candidate from '../../../lib/Candidate';
import Core from '../../../lib/Core';
import Definition, {
  DEFINITION_CATEGORY__ENGAGEMENT_MATCH_STRENGTH,
  IN_OFFICE_REMOTE__FULL_WEEK_ID,
  IN_OFFICE_REMOTE__PART_WEEK_ID,
  IN_OFFICE_REMOTE__REMOTE_ONLY_ID,
  MATCH_TYPE__ADMIN_ADDITIONAL_MATCH,
  MATCH_TYPE__ADMIN_BACK_MATCH,
  MATCH_TYPE__REC_SELF_MATCH
} from '../../../lib/Definition';
import FilterControlLib from '../../../lib/FilterControl';
import {
  getArrayDiff,
  getArrayOfNumberSeries,
  NOT,
  parseBinaryResults,
  YES
} from '../../../lib/GenericTools.lib';
import Job from '../../../lib/Job';
import {
  mapCandidates
} from '../../../lib/models/candidate';
import {
  getNewPrependedEngagementFeedback
} from '../../../lib/models/EngagementFeedback.model';
import {
  mapJobs
} from '../../../lib/models/job';
import {
  Obj
} from '../../../lib/Object.lib';
import FilterLib from '../../../lib/services/Filtering/Filter.lib';
import Store from '../../../lib/Store';
import {
  Str,
  trim
} from '../../../lib/String.lib';
import {
  getLocation
} from '../../../lib/URL.lib';
import filterTestCases from '../../../static/filter-bar__test-cases__AC_2021-07-27_tue.json';

export function jobIsInCandidateList({
  candidate,
  job,
  listName
}) {
  return sanitizeArr(Obj(candidate)[listName]).includes(Obj(job).id);
};

export function getTDModel({
  engagement,
  candidate,
  job,
  whyNoCategories,
  whyNoDetails
}) {
  const YES__ENGAGEMENT = YES(engagement.id);
  const NOT__ENGAGEMENT = NOT(YES__ENGAGEMENT);

  // DEFAULT CREATE/UPDATE MODEL
  const model = {
    stage: STAGE_CONFIRMATION,
    status: STATUS_E_10X10,
    state: ENGAGEMENT__STATE_CLOSED,
    rejectionReason: REJECTION_REASON__10X10__NO_MATCH,
    rejectionReasonAdditionalInfo: trim(whyNoDetails),
    feedback: getNewPrependedEngagementFeedback({
      engagement,
      labels: whyNoCategories
    })
  }

  // IF NOT ENGAGEMENT AND JOB IS PERMITTED OR PITCHED ON CANDIDATE
  // OVERWRITE STATUS, STATE, REJECTION REASON
  if (
    NOT__ENGAGEMENT &&
    (
      jobIsInCandidateList({ candidate, job, listName: 'jobsPermitted' }) ||
      jobIsInCandidateList({ candidate, job, listName: 'jobsPitched' })
    )
  ) {
    model.status = STATUS_W_10X10_NOTIFY_REJECTION;
    model.state = ENGAGEMENT__STATE_OPEN;
    model.rejectionReason = '';
  }

  console.debug('getTDModel',
    {
      YES__ENGAGEMENT,
      candidate,
      job,
      whyNoCategories,
      whyNoDetails
    },
    model
  );

  return model;
}

export function getMLStatsString(options) {
  let mlScoreObject = Obj(Obj(options).mlScoreObject);
  if (mlScoreObject.score === undefined) { return ''; }
  return compile(`
    {{ROUNDED_SCORE}}: MH {{ROUNDED_MH}} (+{{ROUNDED_MH_AND}}-{{ROUNDED_MH_OR}}), NH {{ROUNDED_NH}} (+{{ROUNDED_NH_AND}}-{{ROUNDED_NH_OR}}), PR({{ROUNDED_PR}})
  `)({
    ROUNDED_SCORE: Math.round(mlScoreObject.score),
    ROUNDED_MH: Math.round(mlScoreObject.score_must_haves),
    ROUNDED_MH_AND: Math.round(mlScoreObject.without_not_operator_truthies_divide_by_total_must),
    ROUNDED_MH_OR: Math.round(mlScoreObject.with_not_operator_weightage_to_subtract_must),
    ROUNDED_NH: Math.round(mlScoreObject.score_nice_haves),
    ROUNDED_NH_AND: Math.round(mlScoreObject.without_not_operator_truthies_divide_by_total_nice),
    ROUNDED_NH_OR: Math.round(mlScoreObject.with_not_operator_weightage_to_subtract_nice),
    ROUNDED_PR: Math.round(mlScoreObject.score_platform_rating),
  });
}

export function getMatchInfoString(options) {
  let engagement = Obj(Obj(options).engagement);
  let roundedProbScore = Math.round(engagement.probScore);
  return (
    Str(engagement.matchStrength)
      ? compile(
        `{{PREFIX}}{{MATCH_STRENGTH}}{{MATCHER}}{{ROUNDED_SCORE}}`
      )({
        PREFIX: options.prefix || '',
        MATCH_STRENGTH: Str(engagement.matchStrength) ? Definition.getLabel(DEFINITION_CATEGORY__ENGAGEMENT_MATCH_STRENGTH, engagement.matchStrength) : '',
        MATCHER: Str(engagement.matchedByWho.email) ? ` - ${engagement.matchedByWho.email}` : '',
        ROUNDED_SCORE: Str(roundedProbScore) ? ` - ${roundedProbScore}` : ''
      })
      : ''
  );
}

/**
 * Checks if a match needs to set some flags
 *
 * then set flags into returned object
 *
 * @see story 2572
 */
export function getMatchFlags({ status, candidate, job }) {
  const isRouteCandidateMatch = !!getLocation().match(/\/candidate\/match\//i);
  const isRouteJobMatch = !!getLocation().match(/\/job\/match\//i);
  const engagement = {};
  const validStatuses = [
    STATUS_W_10X10,
    STATUS_W_10X10_REVIEW,
    STATUS_W_10X10_ASK_PERMISSION,
    STATUS_W_10X10_CALIBRATION,
  ];
  const flagIsValidStatus = validStatuses.includes(status);
  console.debug('µ:reviewForAdditionalActions', {
    candidate,
    job,
    status,
    isRouteCandidateMatch,
    isRouteJobMatch,
  });
  const { jobsPermitted = [], jobsPitched = [], jobsDeclined = [] } = candidate;
  const isJobPermitted = jobsPermitted.includes(job.id);
  const isJobPitched = jobsPitched.includes(job.id);
  const isJobDeclined = jobsDeclined.includes(job.id);
  /** CANDIDATE MATCH PAGE BEHAVIOR */
  if (isRouteCandidateMatch) {
    console.debug('µ:reviewForAdditionalActions', {
      isJobPermitted, isJobPitched, isJobDeclined,
    });
    if (isJobPermitted || isJobPitched || isJobDeclined) {
      return setRecruiterSelfMatchFlag({ engagement });
    }
    else {
      return setMatchFlagForAdditionalActions({ engagement });
    }
  }
  /** JOB MATCH PAGE BEHAVIOR */
  else if (isRouteJobMatch) {
    if (flagIsValidStatus) {
      return setMatchFlagForBackMatch({ engagement, candidate });
    }
  }
  if (NOT(engagement.matchType)) {
    Core.failure({
      message: 'Missing matchType',
      data: {
        status,
        candidate,
        job,
        isRouteCandidateMatch,
        isRouteJobMatch,
        flagIsValidStatus,
        isJobPermitted,
        isJobPitched,
        isJobDeclined,
      }
    })
  }
  return engagement;
}

export function setRecruiterSelfMatchFlag({ engagement }) {
  engagement.matchType = MATCH_TYPE__REC_SELF_MATCH;
  return engagement;
}

/**
 * Set flag into returned object
 *
 * Additional Match
 *
 * @see story 2572
 */
export function setMatchFlagForAdditionalActions({ engagement }) {
  engagement.matchType = MATCH_TYPE__ADMIN_ADDITIONAL_MATCH;
  return engagement;
}

/**
 * Set flags into returned object
 *
 * Back Match
 *
 * @see story 2572
 */
export function setMatchFlagForBackMatch({ engagement, candidate }) {
  const today = moment();
  const { updatedAt } = candidate;
  engagement.matchType = MATCH_TYPE__ADMIN_BACK_MATCH;
  engagement.backMatchDays = today.diff(updatedAt, 'days');
  console.debug({ engagement, candidate });
  return engagement;
}

export function presetLocationMenus({
  menu = {},
  inOfficeRemoteTags = [],
  mainEntity = {},
}) {
  if (NOT(mainEntity.___model___)) {
    return false;
  }

  // console.debug('mainEntity', mainEntity);

  let preset = false;

  if (menu.key === 'inOfficeRemoteFlags') {
    preset = menu;
    inOfficeRemoteTags.forEach((tag) => {
      const itemValue = FilterControlLib.getItemValue({
        menu,
        itemLabel: tag.label,
      });
      menu.items[itemValue] =
        menu.items[itemValue] ||
        mainEntity.inOfficeRemoteFlags.includes(tag.id);
    });
  } else if (menu.key === 'candidateLocations') {
    preset = menu;
    mainEntity.candidateLocations.forEach((candidateLocationId) => {
      const candidateLocationLabel = Definition.getLabel(
        'location',
        candidateLocationId
      );
      const itemValue = FilterControlLib.getItemValue({
        menu,
        itemLabel: candidateLocationLabel,
      });
      menu.items[itemValue] =
        menu.items[itemValue] ||
        mainEntity.inOfficeRemoteFlags.includes(
          IN_OFFICE_REMOTE__REMOTE_ONLY_ID
        );
    });
  } else if (menu.key === 'officeLocations') {
    preset = menu;
    mainEntity.officeLocations.forEach((officeLocationId) => {
      const officeLocationLabel = Definition.getLabel(
        'location',
        officeLocationId
      );
      const itemValue = FilterControlLib.getItemValue({
        menu,
        itemLabel: officeLocationLabel,
      });
      menu.items[itemValue] =
        menu.items[itemValue] ||
        YES(
          mainEntity.inOfficeRemoteFlags.includes(
            IN_OFFICE_REMOTE__FULL_WEEK_ID
          ) ||
          mainEntity.inOfficeRemoteFlags.includes(
            IN_OFFICE_REMOTE__PART_WEEK_ID
          )
        );
    });
  }

  return preset;
}

function unitTestingPresetLocationMenusSingle({ mainEntity = {}, items = [] }) {
  const inOfficeRemoteTags = Definition.get('inOfficeRemote');

  const checkedLocationOptionsMenu = {};

  // emulated filter-bar menu.
  [
    { key: 'inOfficeRemoteFlags', items: {} },
    { key: 'candidateLocations', items: {} },
    { key: 'officeLocations', items: {} },
  ].forEach((menu) => {
    // get the presets in menu
    // THIS IS THE OBJECTIVE FUNCTION OF THIS UNIT-TESTING
    const menuOptions = presetLocationMenus({
      menu,
      inOfficeRemoteTags,
      mainEntity,
    }).items;
    checkedLocationOptionsMenu[menu.key] = Object.keys(menuOptions).filter(
      (k) => YES(menuOptions[k])
    );
  });

  // get the tag-ids of each menu-options.
  let {
    inOfficeRemoteFlags = [], // inOfficeRemote tag-ids
    candidateLocations = [], // location tag-ids
    officeLocations = [], // location tag-ids
  } = FilterLib.translateLocationMenus({
    checkedLocationOptionsMenu,
  });

  // the filter-input
  const filter = {
    inOfficeRemoteFlags,
    candidateLocations,
    officeLocations,
  };

  // get the filter-id
  const filterId = parseBinaryResults([
    filter.inOfficeRemoteFlags.includes(IN_OFFICE_REMOTE__REMOTE_ONLY_ID),
    filter.inOfficeRemoteFlags.includes(IN_OFFICE_REMOTE__FULL_WEEK_ID),
    filter.inOfficeRemoteFlags.includes(IN_OFFICE_REMOTE__PART_WEEK_ID),
    !!filter.candidateLocations.length,
    !!filter.officeLocations.length,
  ]);

  // get a filter name (compressed inputs info)
  const filterName = [
    [
      filter.inOfficeRemoteFlags.includes(IN_OFFICE_REMOTE__REMOTE_ONLY_ID)
        ? 'R'
        : 'E',
      filter.inOfficeRemoteFlags.includes(IN_OFFICE_REMOTE__FULL_WEEK_ID)
        ? 'F'
        : 'E',
      filter.inOfficeRemoteFlags.includes(IN_OFFICE_REMOTE__PART_WEEK_ID)
        ? 'P'
        : 'E',
    ].join(''),
    !!filter.candidateLocations.length ? 'S' : 'E',
    !!filter.officeLocations.length ? 'S' : 'E',
  ].join('_');

  // get the test case according to the filter-id
  const testCase = filterTestCases[filterId];

  // once we now the filter has already preset locations
  // we will be overwriting the filter-input locations by the testCase-locations,
  // in order to test the matching of the new filter-id obtained.
  candidateLocations =
    YES(candidateLocations.length) && YES(testCase.candidateLocations.length)
      ? testCase.candidateLocations
      : candidateLocations;
  officeLocations =
    YES(officeLocations.length) && YES(testCase.officeLocations.length)
      ? testCase.officeLocations
      : officeLocations;

  // matching-results
  let matchResults = FilterLib.filterListLocationMenus({
    items,
    inOfficeRemoteFlags,
    candidateLocations,
    officeLocations,
  });

  // setting values to print.
  const _inputItems = items
    .map((v) => v.__unitTestingItemId)
    .filter((n) => !!n);
  const _expected = getArrayOfNumberSeries(testCase.expected);
  const results = matchResults
    .map((v) => v.__unitTestingItemId)
    .filter((n) => !!n);
  const diff = getArrayDiff(_expected, results);
  const _name = mainEntity.jobTitle || mainEntity._name.replace(' ', '');

  // console prints
  console.debug('Unit Testing Matching Pages');
  console.debug('input-items', _inputItems);
  console.debug('test-case.expected-results', _expected);
  console.debug('main-entity.match-results', results);
  !!diff.length
    ? console.debug(
      `\u001b[33m${!diff.length} ${_name} filterId:${filterId} ${filterName} diff\u001b[0m (${diff.length
      }) [${diff.join(',')}]`
    )
    : console.debug(
      `\u001b[32m${!diff.length} ${_name} filterId:${filterId} ${filterName}\u001b[0m`
    );
  if (!!diff.length) {
    console.debug('filter.inOfficeRemoteFlags', inOfficeRemoteFlags);
    console.debug('filter.candidateLocations', candidateLocations);
    console.debug('filter.officeLocations', officeLocations);
    items.forEach((v) => {
      if (diff.includes(v.__unitTestingItemId)) {
        console.debug(
          `${v.jobTitle || v._name} cLoc:[${v.candidateLocations}] oLoc:[${v.officeLocations
          }]`
        );
      }
    });
  }

  console.debug('=============================');

  return checkedLocationOptionsMenu;
}

let candidates = [];
let jobs = [];
export async function unitTestingPresetLocationMenusAll({ job, candidate }) {
  const unitTestingLocationsInMatchPages = Store.get(
    'unitTestingLocationsInMatchPages'
  );
  if (NOT(unitTestingLocationsInMatchPages)) {
    return false;
  }

  if (NOT(candidates.length)) {
    candidates = await Candidate.getActiveCandidates(() => { }, {});
    candidates = mapCandidates(candidates);
    candidates = candidates.filter((v) => YES(v.__unitTestingItemId));
  }
  if (NOT(jobs.length)) {
    jobs = await Job.getActives(() => { });
    jobs = mapJobs(jobs);
    jobs = jobs.filter((v) => YES(v.__unitTestingItemId));
  }

  console.debug({ candidates, jobs });

  if (YES(job)) {
    jobs.forEach((job) => {
      unitTestingPresetLocationMenusSingle({
        mainEntity: job,
        items: candidates,
      });
    });
  } else if (YES(candidate)) {
    candidates.forEach((candidate) => {
      unitTestingPresetLocationMenusSingle({
        mainEntity: candidate,
        items: jobs,
      });
    });
  }
}
